// Drawer primitive — a right-side slide-out panel over a dimmed backdrop.
// Backdrop click and Escape close it; the panel stops propagation. Mirrors the
// Modal primitive's ergonomics (same close affordances), different presentation.

import { KeyboardEvent, MouseEvent, ReactNode, useCallback, useEffect } from 'react';

import { IconClose } from './icons';

export function Drawer({
  title,
  onClose,
  children,
}: {
  title?: string;
  onClose: () => void;
  children: ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const onBackdrop = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );
  const stop = useCallback((e: MouseEvent | KeyboardEvent) => e.stopPropagation(), []);

  return (
    <div className="bz-drawer-backdrop" onClick={onBackdrop} role="presentation">
      <div className="bz-drawer" role="dialog" aria-modal="true" aria-label={title} onClick={stop}>
        <div className="bz-drawer-head">
          {title && <h3 className="bz-drawer-title">{title}</h3>}
          <button type="button" className="bz-drawer-close" onClick={onClose} aria-label="Close">
            <IconClose size={16} />
          </button>
        </div>
        <div className="bz-drawer-body">{children}</div>
      </div>
    </div>
  );
}
